import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { drw, errText, sheetThumbUrl } from "./api";
import { PURPOSE_STYLE, PurposeChip } from "./FieldShopLinks";

/** DRW-6 §4 — sheet → rooms. Only FRS rooms inside the set's scopes (read from the FRS, never
 *  written to it); one or many rooms per sheet; bulk by Building / Floor / Area; cross-area allowed.
 *  The set is not published until this is done (or the unassigned sheets are confirmed). */
export default function FieldShopAssign({ set: initial, onChanged, onClose }) {
  const [set, setSet] = useState(initial);          // the open dialog re-reads its own edits (purpose / scopes / published)
  const [rooms, setRooms] = useState(null);
  const [sheets, setSheets] = useState(set.sheets || []);
  const [picked, setPicked] = useState(new Set(sheets[0] ? [sheets[0].id] : []));
  const [filter, setFilter] = useState({ building: "", floor: "", area: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);     // { purpose, scopes }
  useEffect(() => {
    drw.get(`/projects/${set.project_id}/field-shop/options`, { params: { scopes: (set.scopes || []).join(",") } })
      .then(({ data }) => setRooms(data)).catch((e) => toast.error(errText(e)));
  }, [set.project_id, set.scopes]);

  const groups = useMemo(() => {
    const g = { building: new Set(), floor: new Set(), area: new Set() };
    (rooms?.rooms || []).forEach((r) => { g.building.add(r.building); g.floor.add(r.floor); g.area.add(r.area); });
    return { building: [...g.building].sort(), floor: [...g.floor].sort(), area: [...g.area].sort() };
  }, [rooms]);
  const visible = (rooms?.rooms || []).filter((r) => (!filter.building || r.building === filter.building) && (!filter.floor || r.floor === filter.floor) && (!filter.area || r.area === filter.area));
  const keysOf = (sh) => new Set((sh.room_refs || []).map((r) => r.room_key));
  const pickedSheets = sheets.filter((s) => picked.has(s.id));
  const allHave = (rk) => pickedSheets.length > 0 && pickedSheets.every((s) => keysOf(s).has(rk));

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await drw.put(`/field-shop/sets/${set.id}/rooms`, { assignments: next.map((s) => ({ sheet_id: s.id, room_keys: (s.room_refs || []).map((r) => r.room_key) })) }, { _noQueue: true });
      setSheets(data.sheets); onChanged?.(data);
    } catch (e) { toast.error(errText(e, "Could not save the room assignments")); } finally { setBusy(false); }
  };
  const toggleRoom = (room) => {
    if (!pickedSheets.length) { toast.message("Tick a sheet first"); return; }
    const add = !allHave(room.room_key);
    save(sheets.map((s) => {
      if (!picked.has(s.id)) return s;
      const refs = (s.room_refs || []).filter((r) => r.room_key !== room.room_key);
      return { ...s, room_refs: add ? [...refs, room] : refs };
    }));
  };
  const bulk = (add) => {
    if (!pickedSheets.length) { toast.message("Tick a sheet first"); return; }
    save(sheets.map((s) => {
      if (!picked.has(s.id)) return s;
      const keys = new Set(visible.map((r) => r.room_key));
      const refs = (s.room_refs || []).filter((r) => !keys.has(r.room_key));
      return { ...s, room_refs: add ? [...refs, ...visible] : refs };
    }));
  };
  const publish = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await drw.post(`/field-shop/sets/${set.id}/publish`, { confirm_unassigned: confirm }, { _noQueue: true });
      toast.success("Published — the room links are live"); setSet((cur) => ({ ...cur, ...data })); onChanged?.(data); onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "unassigned") { if (window.confirm(`${d.unassigned} sheet(s) have no room and will not be reachable from any room link. Publish anyway?`)) await publish(true); }
      else toast.error(errText(e));
    } finally { setBusy(false); }
  };
  const patchSet = async (body, confirmOrphans = false) => {
    setBusy(true);
    try {
      const { data } = await drw.patch(`/field-shop/sets/${set.id}`, { ...body, confirm_orphans: confirmOrphans }, { _noQueue: true });
      setSheets(data.sheets); setSet((cur) => ({ ...cur, ...data })); onChanged?.(data); setEditing(null); toast.success("Set updated");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "orphans") { if (window.confirm(`${d.detail}. Those assignments will be removed. Continue?`)) await patchSet(body, true); }
      else toast.error(errText(e));
    } finally { setBusy(false); }
  };
  const unassigned = sheets.filter((s) => !(s.room_refs || []).length).length;
  const st = PURPOSE_STYLE[set.purpose] || PURPOSE_STYLE.INSTALL;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-3" data-testid="shop-assign">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col text-sm">
        <div className="p-3 border-b flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-1 rounded font-bold text-xs ${st.banner}`} data-testid="shop-assign-purpose">{st.label}</span>
          <div className="font-semibold">{set.revision_label || set.file_name}</div>
          <span className="text-xs text-neutral-500">{(set.scopes || []).join(", ")} · {sheets.length} sheet(s) · {unassigned} unassigned{set.published ? " · published" : " · not published"}</span>
          <button type="button" onClick={() => setEditing({ purpose: set.purpose, scopes: set.scopes || [] })} className="text-xs underline" data-testid="shop-edit-set">Edit purpose / scopes</button>
          <button type="button" onClick={onClose} className="ml-auto text-neutral-400 hover:text-neutral-700" data-testid="shop-assign-close">✕</button>
        </div>
        {editing && (
          <div className="p-3 border-b bg-neutral-50 flex flex-wrap items-center gap-3 text-xs" data-testid="shop-edit-panel">
            <span className="font-semibold">Purpose</span>
            {["FM", "INSTALL"].map((p) => (
              <button key={p} type="button" onClick={() => setEditing({ ...editing, purpose: p })} className={`px-2 min-h-9 rounded border font-bold ${editing.purpose === p ? PURPOSE_STYLE[p].banner : "bg-white"}`} data-testid={`shop-edit-purpose-${p}`}>{PURPOSE_STYLE[p].label}</button>
            ))}
            <span className="font-semibold ml-2">Scopes</span>
            {(rooms?.scopes || []).map((s) => (
              <label key={s} className="flex items-center gap-1"><input type="checkbox" checked={editing.scopes.includes(s)} onChange={(e) => setEditing({ ...editing, scopes: e.target.checked ? [...editing.scopes, s] : editing.scopes.filter((x) => x !== s) })} data-testid={`shop-edit-scope-${s}`} /> {s}</label>
            ))}
            <button type="button" disabled={busy} onClick={() => patchSet({ purpose: editing.purpose, scopes: editing.scopes })} className="px-3 min-h-9 rounded bg-neutral-900 text-white" data-testid="shop-edit-save">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="px-3 min-h-9 rounded border bg-white">Cancel</button>
          </div>
        )}
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[18rem_minmax(0,1fr)]">
          <div className="border-r overflow-auto p-2 space-y-1" data-testid="shop-sheets">
            <div className="text-[11px] text-neutral-500 px-1">Tick the sheets, then tap rooms (or a bulk button) →</div>
            {sheets.map((s) => (
              <label key={s.id} className={`flex gap-2 items-start p-1.5 rounded-lg border cursor-pointer ${picked.has(s.id) ? "border-orange-400 bg-orange-50/50" : (s.room_refs || []).length ? "border-neutral-200" : "border-red-300 bg-red-50/40"}`} data-testid={`shop-sheet-${s.id}`} data-rooms={(s.room_refs || []).length}>
                <input type="checkbox" checked={picked.has(s.id)} onChange={(e) => setPicked((p) => { const n = new Set(p); e.target.checked ? n.add(s.id) : n.delete(s.id); return n; })} className="mt-1" data-testid={`shop-sheet-pick-${s.id}`} />
                <img src={sheetThumbUrl(s.id)} alt="" className="w-14 h-14 object-contain bg-neutral-100 rounded border" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs font-semibold truncate">{s.sheet_number}</div>
                  <div className="text-[11px] text-neutral-600 truncate">{s.title}</div>
                  <div className="text-[11px]">{(s.room_refs || []).length ? <span className="text-emerald-700">{(s.room_refs || []).map((r) => r.room_number).join(", ")}</span> : <span className="text-red-700 font-semibold">no room yet</span>}</div>
                </div>
              </label>
            ))}
            <div className="flex gap-1 px-1 pt-1">
              <button type="button" onClick={() => setPicked(new Set(sheets.map((s) => s.id)))} className="text-xs underline">all sheets</button>
              <button type="button" onClick={() => setPicked(new Set())} className="text-xs underline">none</button>
            </div>
          </div>
          <div className="overflow-auto p-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {["building", "floor", "area"].map((f) => (
                <select key={f} value={filter[f]} onChange={(e) => setFilter({ ...filter, [f]: e.target.value })} className="h-9 border rounded px-1 bg-white" data-testid={`shop-filter-${f}`}>
                  <option value="">All {f}s</option>
                  {groups[f].map((v) => <option key={v} value={v}>{v || "(blank)"}</option>)}
                </select>
              ))}
              <button type="button" disabled={busy} onClick={() => bulk(true)} className="px-2 min-h-9 rounded bg-blue-600 text-white font-medium" data-testid="shop-bulk-assign">Assign {visible.length} shown room(s) to {pickedSheets.length} sheet(s)</button>
              <button type="button" disabled={busy} onClick={() => bulk(false)} className="px-2 min-h-9 rounded border bg-white" data-testid="shop-bulk-clear">Clear shown</button>
            </div>
            {!rooms ? <div className="text-neutral-500">Loading FRS rooms…</div> : visible.length === 0 ? <div className="text-neutral-500" data-testid="shop-no-rooms">No FRS rooms in {(set.scopes || []).join(", ")} match this filter.</div> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1" data-testid="shop-rooms">
                {visible.map((r) => {
                  const on = allHave(r.room_key);
                  const some = !on && pickedSheets.some((s) => keysOf(s).has(r.room_key));
                  return (
                    <button key={r.room_key} type="button" disabled={busy} onClick={() => toggleRoom(r)} data-testid={`shop-room-${r.room_number}`} data-on={on ? "1" : undefined}
                            className={`text-left px-2 min-h-11 rounded-lg border text-xs ${on ? "bg-emerald-600 text-white border-emerald-700" : some ? "bg-emerald-50 border-emerald-300" : "bg-white hover:border-orange-400"}`}>
                      <div className="font-semibold">{r.room_number} <span className="font-normal opacity-80">{r.room_name}</span></div>
                      <div className="opacity-70">{[r.building, r.floor && `Fl ${r.floor}`, r.area].filter(Boolean).join(" · ")}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="p-3 border-t flex items-center gap-2 flex-wrap">
          <PurposeChip purpose={set.purpose} scopes={set.scopes} size="sm" />
          <span className="text-xs text-neutral-500">{unassigned ? `${unassigned} sheet(s) still have no room` : "Every sheet has a room"}</span>
          <button type="button" onClick={onClose} className="ml-auto px-3 min-h-11 rounded-md border" data-testid="shop-assign-later">{set.published ? "Done" : "Finish later (not published)"}</button>
          {!set.published && <button type="button" disabled={busy} onClick={() => publish(false)} className="px-4 min-h-11 rounded-md bg-orange-500 text-white font-semibold" data-testid="shop-publish">Publish to the rooms</button>}
        </div>
      </div>
    </div>
  );
}
